import React, { useCallback, useRef, useMemo } from 'react'
import styled from '@emotion/styled'
import { isAvailableDomesticTrainOption } from '~/app/components/Cart/CartOrderHelper'
import {
  ItemContainer,
  DescContainer,
  PriceContainer,
  WarningNotice,
  OptionNameContainer,
  OptionDescriptionContainer,
} from '~/app/components/Cart/CartItem/CartItem.styles'
import { trainClassifyCodeToName } from '~/app/utils'
import { dateWithDayOfWeek } from '~/app/utils/datetime'
import { overtimeHourNotation } from '~/app/utils/TrainHelper'
import {
  useSeatMemo,
  usePassengerMemo,
  useRewardMemo,
  useStatusText,
} from '~/app/components/Cart/CartItem/CartItem.util'
import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import { Typography } from '@yanolja-rnd/ya-com-core'
import { getTrainImage } from '~/app/components/order-converter/converter/TrainOrderConverter'
import FixedTooltip from '~/app/components/common/Tooltip/FixedTooltip'
import { toKRW } from '~/app/utils/krNumberToString'
import CancelPolicyText, {
  CancelPolicyFetchFailText,
} from '~/app/components/common/CancelPolicy/CancelPolicyText'
import { Icon_close, Icon_help, Icon_info } from '@yanolja-rnd/ya-com-system-icon'
import Checkbox from '~/app/components/common/Checkbox/YaComCheckbox'
import { transformTrainNo } from '~/api/utils/train'
import FetchRetryButton from '~/app/components/common/FetchRetry/FetchRetryButton'

/**
 * DomesticLeisureItem component
 * author: jaegeunlee
 * description
 */

const TicketDescriptionWrapper = styled.div<{ disabled: boolean }>`
  color: ${({ disabled, theme }) => (disabled ? theme.color.gray400 : theme.color.gray600)};
  ${Typography('body2').regular};
  margin-top: 0.8rem;
`

const PassengerWrapper = styled.div`
  display: grid;
  gap: 0.4rem;
  margin-top: 0.4rem;
`

const TooltipContent = styled.div`
  margin-top: 1.2rem;
  display: flex;
  align-items: center;

  .text {
    ${Typography('body2').regular};
    color: #e31068;
  }
  .tooltip-icon {
    margin-top: -0.1rem;
    margin-left: 0.2rem;
    height: 1.4rem;
    width: 1.4rem;
    cursor: pointer;
    position: relative;
    bottom: 0.1rem;
  }
  .tooltip-icon svg path {
    fill: #e31068;
  }
`

const RewardContainer = styled.div`
  ${Typography('body2').regular};
  color: ${({ theme }) => theme.color.blue600};
`

const OptionName = styled(OptionNameContainer)`
  width: calc(100% - 1.5rem);
`

const StationName = styled.span`
  ${Typography('body1').regular};
  margin-left: 0.4rem;
`

const TrainDescContainer = styled(DescContainer)`
  display: grid;
  gap: 2rem;
`

const TrainItemCheckbox = styled(Checkbox)<{ isHidden: boolean }>`
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
`

export type TrainOption = Extract<
  CartItemsV3Query['cartItemsV3'][number]['options'][number],
  { __typename?: 'DomesticTrainCartItemOption' }
>
export interface DomesticTrainItemProps {
  option: TrainOption
  pointReward?: number
  coinReward?: number
  onCheck?: (checked: boolean, id: string) => void
  onClose?: (id: string) => void
  onTitleClick?: (id: string) => void
  onCancelRefreshButtonClick?: () => void
}
const DomesticTrainItem: React.FC<DomesticTrainItemProps> = ({
  option,
  pointReward = 0,
  coinReward = 0,
  onCheck,
  onClose,
  onTitleClick,
  onCancelRefreshButtonClick,
}) => {
  const toolTipContent = useRef(
    <>
      <p>기차는 국내숙소와 반드시 함께 예약해야 돼요.</p>
      <p>함께 예약하면 묶음할인을 받을 수 있어요.</p>
    </>
  ).current

  const isDisabled = useMemo(
    () => !isAvailableDomesticTrainOption(option),
    [option.availability, option.outdated]
  )
  const seatsText = useSeatMemo(option)
  const passengerInfo = usePassengerMemo(option)
  const statusText = useStatusText(option)
  const rewardText = useRewardMemo(pointReward, coinReward)
  const defAdultChild = { adult: '성인', child: '아동' }

  const getTrainPackageDiscountRatio = useCallback(
    (trainPrice, packageDiscount) => Math.round((packageDiscount / trainPrice) * 100),
    [option]
  )
  const getTargetFare = useCallback(
    (target, train) => (target === 'adult' ? toKRW(train.adultFare) : toKRW(train.childFare)),
    []
  )

  return (
    <>
      <ItemContainer>
        <TrainDescContainer>
          {option.trains.map((train, index) => (
            <div key={index}>
              <OptionName disabled={isDisabled} onClick={() => onTitleClick?.(option.id)}>
                <span>{overtimeHourNotation(train.runningDate, train.departureAt)}</span>
                <StationName>{train.departureStationName}</StationName>
                <span> ~ </span>
                <span>{overtimeHourNotation(train.runningDate, train.arrivalAt)}</span>
                <StationName>{train.arrivalStationName}</StationName>
              </OptionName>
              <div style={{ marginTop: index > 0 ? 16 : 0 }} className="row">
                <TrainItemCheckbox
                  className="check-icon-wrapper"
                  onClick={() =>
                    !index && onCheck?.(isDisabled ? false : !option.isChosen, option.id)
                  }
                  checked={option.isChosen}
                  disabled={isDisabled}
                  /**
                  왕복 티켓인 경우 체크박스 아이콘은 가는편에만 노출함.
                  오는 편과 UI 위치를 맞추기 위해 아이콘만 노출하지 않고 CheckIconWrapper 만큼 영역을 차지함.
                   */
                  isHidden={!!index}
                />
                <div className="image">{getTrainImage(train.stationTrainClassifyCode).h16}</div>
                <div>
                  <OptionDescriptionContainer disabled={isDisabled}>
                    {dateWithDayOfWeek(train.runningDate)}
                  </OptionDescriptionContainer>
                  <TicketDescriptionWrapper disabled={isDisabled}>
                    <div>
                      <span>
                        {trainClassifyCodeToName(train.stationTrainClassifyCode)}-
                        {transformTrainNo(train.trainNo)}
                      </span>
                      <span className="bar">|</span>
                      <span>{seatsText[index]}</span>
                    </div>
                    <PassengerWrapper>
                      {passengerInfo
                        .find(info => info?.[0]?.goodsNo === train?.goodsNo)
                        ?.map?.(item => (
                          <div key={item.key}>
                            {defAdultChild[item.key]} {getTargetFare(item.key, train)} x{' '}
                            {item.count}명
                          </div>
                        ))}
                    </PassengerWrapper>
                  </TicketDescriptionWrapper>
                  {getTrainPackageDiscountRatio(train.price, train.packageDiscount) > 0 &&
                    !isDisabled && (
                      <FixedTooltip
                        content={toolTipContent}
                        includeCloseIcon={false}
                        stickyTo={'left'}
                        placement={'top'}
                        borderRadius={0.4}
                        customTooltipLeft={-12}
                        pointHorizontalOffset={18.5}
                      >
                        <TooltipContent className="content">
                          <div className="text">{'묶음할인상품'}</div>
                          <div className="tooltip-icon">
                            <Icon_help size={1.5} unit="rem" />
                          </div>
                        </TooltipContent>
                      </FixedTooltip>
                    )}
                  {option.outdated && (
                    <WarningNotice>
                      <Icon_info size={1.6} unit="rem" />
                      <span>상품 정보가 변경되었습니다. 상품을 다시 선택해주세요.</span>
                    </WarningNotice>
                  )}
                  {!isDisabled && !train.cancelPolicySummary && <CancelPolicyFetchFailText />}
                </div>
              </div>
              {!isDisabled && (
                <PriceContainer disabled={isDisabled}>
                  {!train.cancelPolicySummary && (
                    <FetchRetryButton onClick={onCancelRefreshButtonClick} />
                  )}
                  <div className="price-wrapper">
                    <div>
                      <span className="price">{toKRW(train.price)}</span>
                    </div>
                    <RewardContainer>
                      {rewardText && !isDisabled && <span>{rewardText}</span>}
                    </RewardContainer>
                    {train.cancelPolicySummary && (
                      <CancelPolicyText cancelPolicySummary={train.cancelPolicySummary} />
                    )}
                    {statusText && <div className="status-text">{statusText}</div>}
                  </div>
                </PriceContainer>
              )}
            </div>
          ))}
        </TrainDescContainer>
        <div className="icon-wrapper" onClick={() => onClose?.(option.id)}>
          <Icon_close size={1.6} unit="rem" />
        </div>
      </ItemContainer>
      {/*예약 불가능한 상품에 대해서는 총 금액을 노출시킨다*/}
      {isDisabled && (
        <PriceContainer disabled={isDisabled}>
          <div className="price-wrapper">
            <div>
              <span className="price">{toKRW(option.price)}</span>
            </div>
            <RewardContainer>
              {rewardText && !isDisabled && <span>{rewardText}</span>}
            </RewardContainer>
            {statusText && <div className="status-text">{statusText}</div>}
          </div>
        </PriceContainer>
      )}
    </>
  )
}

export default DomesticTrainItem
